import type { CategoryCard } from "@v1/components/left-bar/sections-pane/TemplateCategories";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { Group } from "@v1/components/common/designSystem/Group";
import { DeprecatedEmptyState } from "@v1/components/designSystem/empty-state/EmptyState";
import { HotkeyIndicator } from "@v1/components/designSystem/hotkeys/HotKeyIndicator";
import { SavedTemplateListItem } from "@v1/components/left-bar/sections-pane/SavedTemplateListItem";
import {
  SectionTemplateListItem,
  SectionTemplateListSkeleton,
} from "@v1/components/left-bar/sections-pane/SectionTemplateListItem";
import useInfiniteTemplates from "@v1/hooks/useInfiniteTemplates";
import { selectProjectId } from "@v1/reducers/core-reducer";
import { useEditorSelector } from "@v1/store/store";
import { Bookmark, Search } from "lucide-react";
import InfiniteScroll from "react-infinite-scroll-component";

const SectionTemplatesList = ({
  category,
  searchString,
}: {
  category?: CategoryCard;
  searchString?: string;
}) => {
  const selectedCategories = category?.id ? [category.id] : [];

  const {
    componentTemplatesList,
    fetchNextPage,
    isLoadingInitialPage,
    hasNextPage,
  } = useInfiniteTemplates({
    searchText: searchString,
    includeComponent: true,
    selectedCategories: selectedCategories,
    scope: "global",
    componentCategoryType: ["section"],
    pageSize: 10,
    selectedIndustries: [],
    includeArchivedTemplates: false,
  });

  if (isLoadingInitialPage) {
    return <SectionTemplateListSkeleton hideTitle={Boolean(searchString)} />;
  }

  if (componentTemplatesList?.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2">
        <DeprecatedEmptyState
          title="No Results"
          description={
            searchString
              ? `No results matching "${searchString}"`
              : "No results found"
          }
          icon={<Search className="icon-size-default" />}
          size="sm"
          UNSAFE_className="py-[15%]"
        />
      </div>
    );
  }

  return (
    <InfiniteScroll
      dataLength={componentTemplatesList?.length ?? 0}
      next={() => {
        void fetchNextPage();
      }}
      hasMore={hasNextPage}
      scrollableTarget={
        searchString
          ? "sections-pane-search-wrapper"
          : "sections-pane-template-list-wrapper"
      }
      loader={
        <div className={twMerge(searchString ? "mt-[-28px]" : "p-3 pt-0")}>
          <SectionTemplateListSkeleton hideTitle count={1} />
        </div>
      }
      className="no-scrollbar"
      style={{
        overflow: "visible",
      }}
    >
      <div
        className={twMerge("flex flex-col gap-3", searchString ? "" : "p-3")}
      >
        <Group
          key={category?.name ?? ""}
          name={category?.name ?? ""}
          header={
            category?.name ? (
              <span className="text-xs font-semibold">{category?.name}</span>
            ) : null
          }
        >
          <ul
            className={twMerge(
              "mb-8 flex flex-col gap-2",
              searchString ? "" : "mt-3",
            )}
          >
            {componentTemplatesList?.map((componentTemplate) => {
              return (
                <SectionTemplateListItem
                  key={componentTemplate.id}
                  componentTemplate={componentTemplate}
                />
              );
            })}
          </ul>
        </Group>
      </div>
    </InfiniteScroll>
  );
};

const SavedTemplatesList = () => {
  const projectId = useEditorSelector(selectProjectId);
  const {
    componentTemplatesList,
    hasNextPage,
    fetchNextPage,
    isLoadingInitialPage,
  } = useInfiniteTemplates({
    storeId: projectId,
    scope: "store",
    includeComponent: true,
    componentCategoryType: ["section"],
    pageSize: 10,
    selectedIndustries: [],
    selectedCategories: [],
    includeArchivedTemplates: false,
  });

  if (isLoadingInitialPage) {
    return <SectionTemplateListSkeleton />;
  }

  if (componentTemplatesList?.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2">
        <DeprecatedEmptyState
          title="No Saved Sections"
          description="Save a section by selecting it and pressing Command+Shift+S"
          icon={<Bookmark />}
          size="sm"
          UNSAFE_className="py-[15%]"
        />
        <HotkeyIndicator
          hotkey="saveComponentTemplate"
          title=""
          className="mt-[-50px]"
        />
      </div>
    );
  }

  return (
    <InfiniteScroll
      dataLength={componentTemplatesList?.length ?? 0}
      next={() => void fetchNextPage()}
      hasMore={hasNextPage}
      scrollableTarget="sections-pane-template-list-wrapper"
      loader={
        <div className="p-3 pt-0">
          <SectionTemplateListSkeleton hideTitle count={1} />
        </div>
      }
      style={{
        overflow: "visible",
      }}
    >
      <div className="flex flex-col p-3">
        <Group
          name="Saved"
          isDefaultOpen
          header={<span className="text-xs font-semibold">Saved</span>}
        >
          <ul className="mt-3 flex flex-col gap-2">
            {componentTemplatesList?.map((componentTemplate) => {
              return (
                <SavedTemplateListItem
                  key={componentTemplate.id}
                  componentTemplate={componentTemplate}
                />
              );
            })}
          </ul>
        </Group>
      </div>
    </InfiniteScroll>
  );
};

const TemplateList = ({
  category,
  searchString,
}: {
  category?: CategoryCard;
  searchString?: string;
}) => {
  if (category?.value === "saved") {
    return <SavedTemplatesList />;
  }

  return (
    <SectionTemplatesList category={category} searchString={searchString} />
  );
};

export default TemplateList;
